import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Paperclip, FilePdf, FileXls, FileImage, File as FileIcon, Eye, DownloadSimple, Trash, UploadSimple, CircleNotch } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi } from './mfgApi';
import AttachmentViewer from './AttachmentViewer';

const VIEWABLE = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'xlsx', 'xls', 'csv'];
const ACCEPT = '.pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.webp';

const fmtSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const FileTypeIcon = ({ ext }) => {
  if (ext === 'pdf') return <FilePdf size={20} weight="duotone" className="text-rose-500 shrink-0" />;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileXls size={20} weight="duotone" className="text-emerald-600 shrink-0" />;
  if (VIEWABLE.includes(ext)) return <FileImage size={20} weight="duotone" className="text-blue-500 shrink-0" />;
  return <FileIcon size={20} weight="duotone" className="text-zinc-400 shrink-0" />;
};

export default function AttachmentsPanel({ entityType, entityId, canWrite = false, title, hint, testid = 'attachments' }) {
  const { t } = useT();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    if (!entityId) return;
    setLoading(true);
    mfgApi.attachments(entityType, entityId)
      .then(setFiles).catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await mfgApi.uploadAttachment(entityType, entityId, file);
      toast.success(t('mfg.attach.uploaded'));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('mfg.error'));
    } finally { setUploading(false); }
  };

  const openFile = (att) => {
    if (VIEWABLE.includes(att.ext)) setViewing(att);
    else download(att);
  };

  const download = async (att) => {
    setBusyId(att.id);
    try {
      const blob = await mfgApi.attachmentBlob(att.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = att.original_filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error(t('mfg.error')); }
    finally { setBusyId(null); }
  };

  const remove = async (att) => {
    if (!window.confirm(t('mfg.attach.confirmDelete', { name: att.original_filename }))) return;
    setBusyId(att.id);
    try {
      await mfgApi.deleteAttachment(att.id);
      toast.success(t('mfg.attach.deleted'));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t('mfg.error')); }
    finally { setBusyId(null); }
  };

  return (
    <section className="rounded-lg border border-zinc-200 overflow-hidden" data-testid={testid}>
      <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-1.5">
          <Paperclip size={15} className="text-orange-500" /> {title || t('mfg.attach.title')}
          {files.length > 0 && <span className="text-xs font-normal text-zinc-400">({files.length})</span>}
        </h3>
        {canWrite && entityId && (
          <>
            <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={uploading}
              onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload-btn`}>
              {uploading ? <CircleNotch size={13} className="mr-1 animate-spin" /> : <UploadSimple size={13} className="mr-1" />}
              {t('mfg.attach.upload')}
            </Button>
          </>
        )}
      </div>
      {hint && <p className="px-3 pt-2 text-[11px] text-zinc-400">{hint}</p>}
      {loading ? (
        <p className="px-3 py-5 text-center text-xs text-zinc-400">…</p>
      ) : files.length === 0 ? (
        <p className="px-3 py-5 text-center text-xs text-zinc-400" data-testid={`${testid}-empty`}>{t('mfg.attach.empty')}</p>
      ) : (
        <ul className="divide-y divide-zinc-200" data-testid={`${testid}-list`}>
          {files.map((att) => (
            <li key={att.id} className="flex items-center gap-2 px-3 py-2" data-testid={`${testid}-row-${att.id}`}>
              <FileTypeIcon ext={att.ext} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-800 truncate">{att.original_filename}</p>
                <p className="text-[11px] text-zinc-400">
                  {fmtSize(att.size)}{att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ''}
                  {att.document_id && (
                    <span className="ml-1.5 px-1 py-0.5 rounded border border-teal-200 bg-teal-50 text-teal-700 text-[9px]"
                      title="ファイル本体はドキュメントアプリで管理されています（下の関連書類と同じファイルです）"
                      data-testid={`${testid}-dms-${att.id}`}>ドキュメント管理</span>
                  )}
                </p>
              </div>
              {VIEWABLE.includes(att.ext) && (
                <button onClick={() => openFile(att)} disabled={busyId === att.id} title={t('mfg.attach.view')}
                  className="p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-orange-600 disabled:opacity-40" data-testid={`${testid}-view-${att.id}`}>
                  <Eye size={16} />
                </button>
              )}
              <button onClick={() => download(att)} disabled={busyId === att.id} title={t('mfg.attach.download')}
                className="p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-orange-600 disabled:opacity-40" data-testid={`${testid}-download-${att.id}`}>
                <DownloadSimple size={16} />
              </button>
              {canWrite && (
                <button onClick={() => remove(att)} disabled={busyId === att.id} title={t('mfg.common.delete')}
                  className="p-1.5 rounded-md text-zinc-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40" data-testid={`${testid}-delete-${att.id}`}>
                  <Trash size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <AttachmentViewer open={!!viewing} onClose={() => setViewing(null)} attachment={viewing} />
    </section>
  );
}
